/* SUMMARY
  * Imports
  * UserPreferences: le contrat transporté
  * fetchUserPreferences: lecture serveur
  * saveTablePageSize: écriture serveur puis miroir
*/

/* Imports */
import { api } from "../api/api";
import { isValidTablePageSize, writeTablePageSize } from "./tablePageSize";
/***/

/**
 * Accès aux préférences d'affichage de l'utilisateur connecté.
 *
 * La ressource est `users/me/preferences`, sans identifiant dans l'URL : l'appelant est déduit du
 * jeton côté serveur. C'est ce qui rend impossible d'écrire la préférence de quelqu'un d'autre, et
 * c'est aussi pour cela qu'elle ne passe pas par `admin/users/{id}` — ce contrôleur est réservé aux
 * rôles administrateur, et le planificateur, qui est l'utilisateur visé, y reçoit un 403.
 */

/** Ce que le serveur sait d'une préférence. `null` signifie « jamais choisi », pas « 20 ». */
export interface UserPreferences {
  tablePageSize: number | null;
}

/**
* fetchUserPreferences: les préférences du compte connecté.
*
* La réponse est vérifiée plutôt que castée : tant que le back n'est pas déployé, cet appel peut
* renvoyer une page d'erreur ou un corps vide, et une valeur inattendue qui remonterait jusqu'au
* `slice()` d'un tableau afficherait une liste vide sans rien expliquer.
*/
export const fetchUserPreferences = async (): Promise<UserPreferences> => {
  const response = await api.get("users/me/preferences");
  const raw = (response as Partial<UserPreferences> | null)?.tablePageSize;

  return { tablePageSize: isValidTablePageSize(raw) ? raw : null };
};

/**
* saveTablePageSize: enregistre le choix côté serveur, puis aligne le miroir local.
*
* L'ordre compte. Écrire le miroir d'abord afficherait la nouvelle taille sur tous les tableaux
* alors que le serveur a refusé l'écriture : l'utilisateur croirait son choix enregistré et le
* retrouverait perdu au prochain navigateur. En cas de rejet, la promesse remonte et le miroir n'a
* pas bougé.
*
* @param size - taille choisie, ou `null` pour revenir au défaut
*/
export const saveTablePageSize = async (size: number | null): Promise<void> => {
  await api.put("users/me/preferences", { tablePageSize: size });
  writeTablePageSize(size);
};
